import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { TrashIcon, XCircleIcon, MailIcon } from "@heroicons/react/outline";
import { upsertUserAsync } from "../../../features/user/userSlice";
import { updateCurrentUser, logoutAsync } from "../../../features/auth/authSlice";
import { useToastNotifications } from "../../../features/toast-notification/toastNotificationSlice";
import Button from "../../misc/Button";
import ExpandableMenu, { ExpandableMenuButton } from "../../general/ExpandableMenu";
import DeleteConfirmationModal from "../../pages/dashboard/DeleteConfirmationModal";

const TeamManagement = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector(state => state.auth.currentUser);
  const { flashSuccessNotification, flashErrorNotification } = useToastNotifications();
  const [isConverting, setIsConverting] = useState(false);
  const [email, setEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [resendingMemberId, setResendingMemberId] = useState(null);
  const [deleteModalShowing, setDeleteModalShowing] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [confirmingConvertToPersonal, setConfirmingConvertToPersonal] = useState(false);
  const [confirmingLeaveTeam, setConfirmingLeaveTeam] = useState(false);

  const handleConvertToTeamAccount = async () => {
    setIsConverting(true);
    try {
      const result = await dispatch(
        upsertUserAsync({
          user: {
            id: currentUser.id,
            isTeamAccount: true
          }
        })
      );

      if (result.error) {
        flashErrorNotification("Failed to convert to team account");
        return;
      }

      // Update current user in place (avoid another fetch and jank from chaging the whole user object)
      dispatch(updateCurrentUser({ isTeamAccount: true }));
      flashSuccessNotification("Successfully converted to team account");
    } catch (error) {
      console.error("Failed to convert to team account", error);
      flashErrorNotification("Failed to convert to team account");
    } finally {
      setIsConverting(false);
    }
  };

  const handleConvertToPersonalAccount = async () => {
    setIsConverting(true);

    try {
      // First, delete all team members
      const teamMembers = currentUser.teamMembers || [];
      if (teamMembers.length > 0) {
        const deletePromises = teamMembers.map(member =>
          axios.delete(`/api/team/member/${member.id}`)
        );

        await Promise.all(deletePromises);
        flashSuccessNotification(
          `Deleted ${teamMembers.length} team member${teamMembers.length > 1 ? "s" : ""}`
        );
      }

      // Then convert to personal account
      const result = await dispatch(
        upsertUserAsync({
          user: {
            id: currentUser.id,
            isTeamAccount: false
          }
        })
      );

      if (result.error) {
        flashErrorNotification("Failed to convert to personal account");
        return;
      }

      dispatch(updateCurrentUser({ isTeamAccount: false, teamMembers: [] }));
      flashSuccessNotification("Successfully converted to personal account");
    } catch (error) {
      console.error("Failed to convert to personal account", error);
      flashErrorNotification("Failed to convert to personal account");
    } finally {
      setIsConverting(false);
      setConfirmingConvertToPersonal(false);
    }
  };

  const handleInviteTeamMember = async () => {
    const emailTrimmed = email.trim();
    if (!emailTrimmed) return;

    setIsInviting(true);
    try {
      const response = await axios.post("/api/team/invite", {
        email: emailTrimmed,
        windowLocation: `${window.location.protocol}//${window.location.host}`
      });
      dispatch(
        updateCurrentUser({
          teamMembers: [
            ...currentUser.teamMembers,
            {
              id: response.data.id,
              email: emailTrimmed,
              status: "pending"
            }
          ]
        })
      ); // Trigger re-render to show new member
      flashSuccessNotification(`Invitation sent to ${email}`);
      setEmail("");
    } catch (error) {
      const userExists = error.response?.data?.error?.message === "User already exists";
      const message = userExists
        ? "Cannot add existing user. Please try another email."
        : "Failed to send invitation";
      flashErrorNotification(message);
    } finally {
      setIsInviting(false);
    }
  };

  const handleLeaveTeam = async () => {
    setIsLeavingTeam(true);
    try {
      await axios.delete(`/api/user/${currentUser.id}`);
      flashSuccessNotification("Successfully left team");
      // Log out and redirect to home
      dispatch(logoutAsync());
      history.push("/");
    } catch (error) {
      console.error("Failed to leave team", error);
      flashErrorNotification("Failed to leave team. Please try again.");
      setIsLeavingTeam(false);
      setConfirmingLeaveTeam(false);
    }
  };

  const openRemoveMemberModal = (member, isPending) => {
    setMemberToRemove({ ...member, isPending });
    setDeleteModalShowing(true);
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    const { id: memberId, isPending } = memberToRemove;
    setRemovingMemberId(memberId);
    setDeleteModalShowing(false);

    try {
      await axios.delete(`/api/team/member/${memberId}`);

      // Update current user's team members list
      const updatedMembers = currentUser.teamMembers.filter(m => m.id !== memberId);
      dispatch(updateCurrentUser({ teamMembers: updatedMembers }));

      flashSuccessNotification(
        isPending ? "Invitation cancelled successfully" : "Team member removed successfully"
      );
    } catch (error) {
      console.error("Failed to remove team member", error);
      flashErrorNotification("Failed to remove team member. Please try again.");
    } finally {
      setRemovingMemberId(null);
      setMemberToRemove(null);
    }
  };

  const handleResendInvite = async (memberId, memberEmail) => {
    setResendingMemberId(memberId);
    try {
      await axios.post("/api/team/invite", {
        email: memberEmail,
        windowLocation: `${window.location.protocol}//${window.location.host}`
      });
      flashSuccessNotification(`Invitation resent to ${memberEmail}`);
    } catch (error) {
      console.error("Failed to resend invitation", error);
      flashErrorNotification("Failed to resend invitation. Please try again.");
    } finally {
      setResendingMemberId(null);
    }
  };

  // Team member view (user belongs to a team)
  const isTeamMember = currentUser.TeamId && currentUser.teamOwner;
  if (isTeamMember) {
    return (
      <div className="mt-6" data-testid="team-member-section">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Team Member</h3>
        <p className="text-sm text-gray-600 mb-4">
          You are a member of <span className="font-semibold">{currentUser.teamOwner.name}</span>
          's team. All content you create is owned by the team account.
        </p>
        <div className="mt-6 mb-4">
          <h4 className="text-sm font-medium mb-2">Danger Zone</h4>
          {!confirmingLeaveTeam ? (
            <>
              <p className="text-xs text-red-700 mb-2">
                ⚠️ Warning: Leaving the team will delete your account.
              </p>
              <Button
                onClick={() => setConfirmingLeaveTeam(true)}
                variant="danger-outline"
                disabled={isLeavingTeam}
                data-testid="leave-team-button"
              >
                Leave Team
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-red-900 font-medium mb-2">
                Are you sure? This will permanently delete your account.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={handleLeaveTeam}
                  variant="danger"
                  disabled={isLeavingTeam}
                  data-testid="confirm-leave-team-button"
                >
                  {isLeavingTeam ? "Leaving..." : "Confirm Leave"}
                </Button>
                <Button
                  onClick={() => setConfirmingLeaveTeam(false)}
                  variant="secondary"
                  disabled={isLeavingTeam}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Personal account view (not a team account yet)
  if (!currentUser.isTeamAccount) {
    return (
      <div className="mt-6" data-testid="convert-to-team-section">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Team Account</h3>
        <p className="text-sm text-gray-600 mb-4">
          Convert your account to a team account to invite team members who can collaborate and
          create content under your account.
        </p>
        <Button
          onClick={handleConvertToTeamAccount}
          variant="primary"
          disabled={isConverting}
          data-testid="convert-to-team-button"
        >
          {isConverting ? "Converting..." : "Convert to Team Account"}
        </Button>
      </div>
    );
  }

  // Team owner view (has team account)
  const teamMembers = currentUser.teamMembers || [];
  const pendingMembers = teamMembers.filter(m => m.status === "pending");
  const activeMembers = teamMembers.filter(m => m.status === "active");

  return (
    <div className="mt-6" data-testid="team-management-section">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Team Management</h3>

      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Invite Team Member</h4>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleInviteTeamMember();
              }
            }}
            placeholder="team-member@email.com"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            disabled={isInviting}
            data-testid="invite-email-input"
          />
          <Button
            type="button"
            onClick={handleInviteTeamMember}
            variant="primary"
            disabled={isInviting || !email.trim()}
          >
            {isInviting ? "Sending..." : "Send Invite"}
          </Button>
        </div>
      </div>

      {teamMembers.length > 0 && (
        <div className="mb-6" data-testid="team-members-list">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Team Members</h4>

          {activeMembers.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Active Members ({activeMembers.length})</p>
              <div className="space-y-2">
                {activeMembers.map(member => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2"
                    data-testid="active-team-member"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {member.name || "No name"}
                      </p>
                      <p className="text-xs text-gray-500">{member.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                        Active
                      </span>
                      {removingMemberId === member.id ? (
                        <span className="text-xs text-gray-500 px-3 py-2">Removing...</span>
                      ) : (
                        <ExpandableMenu data-testid={`member-actions-${member.id}`}>
                          <ExpandableMenuButton
                            onClick={() => openRemoveMemberModal(member, false)}
                          >
                            <div className="text-red-500 flex items-center">
                              <TrashIcon className="h-5 mr-2 inline" />
                              Remove
                            </div>
                          </ExpandableMenuButton>
                        </ExpandableMenu>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pendingMembers.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">
                Pending Invitations ({pendingMembers.length})
              </p>
              <div className="space-y-2">
                {pendingMembers.map(member => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2"
                    data-testid="pending-team-member"
                  >
                    <div>
                      <p className="text-sm text-gray-600">{member.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                        Pending
                      </span>
                      {removingMemberId === member.id ? (
                        <span className="text-xs text-gray-500 px-3 py-2">Cancelling...</span>
                      ) : resendingMemberId === member.id ? (
                        <span className="text-xs text-gray-500 px-3 py-2">Resending...</span>
                      ) : (
                        <ExpandableMenu data-testid={`pending-member-actions-${member.id}`}>
                          <ExpandableMenuButton
                            onClick={() => handleResendInvite(member.id, member.email)}
                          >
                            <div className="text-gray-800 flex items-center">
                              <MailIcon className="h-5 mr-2 inline" />
                              Resend Invitation
                            </div>
                          </ExpandableMenuButton>
                          <ExpandableMenuButton onClick={() => openRemoveMemberModal(member, true)}>
                            <div className="text-red-500 flex items-center">
                              <XCircleIcon className="h-5 mr-2 inline" />
                              Cancel Invitation
                            </div>
                          </ExpandableMenuButton>
                        </ExpandableMenu>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mb-4">
        <h4 className="text-sm font-medium mb-2">Danger Zone</h4>

        {!confirmingConvertToPersonal ? (
          <>
            <p className="text-xs text-red-700 mb-2">
              ⚠️ Warning: This will delete all your team members and pending invites.
            </p>
            <Button
              onClick={() => setConfirmingConvertToPersonal(true)}
              variant="danger-outline"
              disabled={isConverting}
              data-testid="convert-to-personal-button"
            >
              Revert to Personal Account
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-red-900 font-medium mb-2">
              Are you sure? This will permanently delete all team members and pending invites.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleConvertToPersonalAccount}
                variant="danger"
                disabled={isConverting}
                data-testid="confirm-convert-to-personal-button"
              >
                {isConverting ? "Reverting..." : "Confirm Revert"}
              </Button>
              <Button
                onClick={() => setConfirmingConvertToPersonal(false)}
                variant="secondary"
                disabled={isConverting}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>

      <DeleteConfirmationModal
        showing={deleteModalShowing}
        closeModal={() => {
          setDeleteModalShowing(false);
          setMemberToRemove(null);
        }}
        deleteAsync={handleRemoveMember}
        title={memberToRemove?.isPending ? "Cancel Invitation" : "Remove Team Member"}
        text={
          memberToRemove?.isPending
            ? `Are you sure you want to cancel the invitation for ${memberToRemove.email}?`
            : `Are you sure you want to remove ${
                memberToRemove?.name || memberToRemove?.email
              } from your team?\n\n⚠️ WARNING: This will permanently delete their account.`
        }
        confirmButtonName={memberToRemove?.isPending ? "Cancel Invitation" : "Remove"}
      />
    </div>
  );
};

export default TeamManagement;
